"use client";

import axios from "axios";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm, type Resolver } from "react-hook-form";
import { Check, ScanLine, X } from "lucide-react";
import { api } from "@/lib/axios";
import type { ProductDetail, ProductRow } from "@/lib/types/product";
import type { CategoryOption } from "@/lib/categories";
import type { Branch } from "@/hooks/api/useBranches";
import type { Unit } from "@/hooks/api/useUnits";
import { productFormSchema, type ProductFormValues } from "@/lib/schemas/product";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCreateProduct,
  useUpdateProduct,
  useUploadProductImage,
} from "@/hooks/api/useProductMutations";
import { cn } from "@/lib/utils";

/** Segmented pill — same visual language as Settings branch + admin roles/users. */
function BinaryPillToggle({
  value,
  disabled,
  onChange,
  offLabel,
  onLabel,
  ariaLabel,
  offTitle,
  onTitle,
}: {
  value: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  offLabel: string;
  onLabel: string;
  ariaLabel?: string;
  offTitle?: string;
  onTitle?: string;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex shrink-0 rounded-full border border-border/80 bg-muted/45 p-0.5 shadow-inner dark:bg-muted/25",
        "ring-offset-background",
      )}
    >
      <button
        type="button"
        role="radio"
        aria-checked={!value}
        title={offTitle}
        disabled={disabled}
        onClick={() => {
          if (value) {
            onChange(false);
          }
        }}
        className={cn(
          "flex min-h-8 items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide transition-all duration-200 outline-none",
          "focus-visible:ring-2 focus-visible:ring-violet-500/45 focus-visible:ring-offset-1",
          "disabled:pointer-events-none disabled:opacity-40",
          !value
            ? "bg-gradient-to-br from-slate-700 to-slate-900 text-white shadow-sm dark:from-slate-600 dark:to-slate-800"
            : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
        )}
      >
        <X className="size-3 shrink-0 opacity-90" strokeWidth={2.5} />
        {offLabel}
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={value}
        title={onTitle}
        disabled={disabled}
        onClick={() => {
          if (!value) {
            onChange(true);
          }
        }}
        className={cn(
          "flex min-h-8 items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide transition-all duration-200 outline-none",
          "focus-visible:ring-2 focus-visible:ring-violet-500/45 focus-visible:ring-offset-1",
          "disabled:pointer-events-none disabled:opacity-40",
          value
            ? "bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-sm"
            : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
        )}
      >
        <Check className="size-3 shrink-0 opacity-90" strokeWidth={2.5} />
        {onLabel}
      </button>
    </div>
  );
}

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  product: ProductRow | ProductDetail | null;
  categories: CategoryOption[];
  units: Unit[];
  branches: Branch[];
  /** When false, branch field hidden and branch_id saved as null. */
  branchesEnabled?: boolean;
};

function defaultsFrom(
  p: ProductRow | ProductDetail | null,
  branches: Branch[],
  branchesEnabled: boolean,
): ProductFormValues {
  const b = branchesEnabled ? branches[0]?.id : null;
  if (!p) {
    return {
      name: "",
      brand: "",
      sku: "",
      barcode: "",
      category_id: null,
      unit_id: null,
      hsn_code: "",
      gst_rate: 0,
      cost_price: 0,
      selling_price: 0,
      mrp: 0,
      stock_qty: 0,
      branch_id: b ?? null,
      is_active: true,
    };
  }

  const num = (x: unknown) => {
    const n = typeof x === "number" ? x : Number(x);

    return Number.isFinite(n) ? n : 0;
  };

  const idOrNull = (x: unknown): number | null => {
    if (x === undefined || x === null || x === "") {
      return null;
    }
    const n = Number(x);

    return Number.isFinite(n) ? n : null;
  };

  return {
    name: String(p.name ?? ""),
    brand: p.brand != null && p.brand !== "" ? String(p.brand) : "",
    sku: p.sku != null && p.sku !== "" ? String(p.sku) : "",
    barcode: p.barcode != null && p.barcode !== "" ? String(p.barcode) : "",
    category_id: idOrNull(p.category_id),
    unit_id: idOrNull(p.unit_id),
    hsn_code: p.hsn_code != null ? String(p.hsn_code) : "",
    gst_rate: num(p.gst_rate),
    cost_price: num(p.cost_price),
    selling_price: num(p.selling_price),
    mrp: num(p.mrp),
    stock_qty: num((p as unknown as { stock_qty?: unknown }).stock_qty),
    branch_id: idOrNull(p.branch_id) ?? b ?? null,
    is_active: Number(p.is_active) === 1,
  };
}

export function ProductFormModal({
  open,
  onOpenChange,
  product,
  categories,
  units,
  branches,
  branchesEnabled = true,
}: Props) {
  const create = useCreateProduct();
  const update = useUpdateProduct();
  const uploadImg = useUploadProductImage();
  const saveBusy = create.isPending || update.isPending;
  const [saveError, setSaveError] = useState<string | null>(null);
  const [mrpInclusive, setMrpInclusive] = useState(true);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanBuf, setScanBuf] = useState("");
  const [scanBusy, setScanBusy] = useState(false);
  const [scanInfo, setScanInfo] = useState<string | null>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema) as Resolver<ProductFormValues>,
    defaultValues: defaultsFrom(null, branches, branchesEnabled),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const mrpVal = form.watch("mrp");
  const gstVal = form.watch("gst_rate");

  useEffect(() => {
    if (!mrpInclusive) {
      return;
    }
    const mrpRaw = mrpVal as unknown;
    const mrpEmpty =
      mrpRaw === "" || mrpRaw === null || mrpRaw === undefined || Number.isNaN(Number(mrpRaw));
    const mrp = mrpEmpty ? 0 : Number(mrpRaw);
    const gst = Number(gstVal) || 0;
    const cur = Number(form.getValues("selling_price")) || 0;
    if (mrp <= 0) {
      if (cur !== 0) {
        form.setValue("selling_price", 0, { shouldDirty: true, shouldValidate: false });
      }

      return;
    }
    const sell = Math.round((mrp / (1 + gst / 100)) * 100) / 100;
    if (Math.abs(cur - sell) > 0.005) {
      form.setValue("selling_price", sell, { shouldDirty: true, shouldValidate: false });
    }
  }, [mrpInclusive, mrpVal, gstVal, form]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSaveError(null);
    form.reset(defaultsFrom(product, branches, branchesEnabled));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset form only when opening / switching record
  }, [open, product?.id, branchesEnabled]);

  // When creating new product, branches may load after modal opens.
  // Set branch_id default without wiping typed fields.
  useEffect(() => {
    if (!branchesEnabled || !open || product) {
      return;
    }
    const b = branches[0]?.id;
    if (!b) {
      return;
    }
    const cur = form.getValues("branch_id");
    if (cur == null) {
      form.setValue("branch_id", b, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
    }
  }, [open, product, branches, branchesEnabled, form]);

  function openScan() {
    setScanBuf("");
    setScanInfo(null);
    setScanOpen(true);
    setTimeout(() => scanInputRef.current?.focus(), 0);
  }

  async function applyScannedBarcode(rawCode: string) {
    const code = rawCode.trim();
    if (code === "") {
      return;
    }
    form.setValue("barcode", code, { shouldDirty: true, shouldValidate: true });

    if (product) {
      setScanInfo(`Barcode set: ${code}`);
      setScanOpen(false);

      return;
    }

    setScanBusy(true);
    setScanInfo("Looking up product…");
    try {
      const { data } = await api.get<{ product: Record<string, unknown> }>(
        `/api/products/barcode/${encodeURIComponent(code)}`,
      );
      const p = data.product;
      if (p && typeof p === "object") {
        const numOr = (k: string, fallback: number) => {
          const n = Number((p as Record<string, unknown>)[k]);

          return Number.isFinite(n) ? n : fallback;
        };
        const strOr = (k: string, fallback: string) => {
          const v = (p as Record<string, unknown>)[k];

          return v == null ? fallback : String(v);
        };
        form.setValue("name", strOr("name", form.getValues("name")), { shouldDirty: true });
        form.setValue("brand", strOr("brand", form.getValues("brand") ?? ""), { shouldDirty: true });
        form.setValue("sku", strOr("sku", form.getValues("sku") ?? ""), { shouldDirty: true });
        form.setValue("hsn_code", strOr("hsn_code", form.getValues("hsn_code") ?? ""), { shouldDirty: true });
        form.setValue("gst_rate", numOr("gst_rate", Number(form.getValues("gst_rate") ?? 0)), { shouldDirty: true });
        form.setValue("cost_price", numOr("cost_price", Number(form.getValues("cost_price") ?? 0)), { shouldDirty: true });
        form.setValue("selling_price", numOr("selling_price", Number(form.getValues("selling_price") ?? 0)), { shouldDirty: true });
        form.setValue("mrp", numOr("mrp", Number(form.getValues("mrp") ?? 0)), { shouldDirty: true });
        const catId = Number((p as Record<string, unknown>).category_id);
        if (Number.isFinite(catId) && catId > 0) {
          form.setValue("category_id", catId, { shouldDirty: true });
        }
        const unitId = Number((p as Record<string, unknown>).unit_id);
        if (Number.isFinite(unitId) && unitId > 0) {
          form.setValue("unit_id", unitId, { shouldDirty: true });
        }
        setScanInfo("Product found — details prefilled. Adjust stock + price as needed.");
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setScanInfo("New barcode (no existing product). Fill details and Save.");
      } else {
        const data = axios.isAxiosError(err)
          ? (err.response?.data as { error?: string } | undefined)
          : undefined;
        setScanInfo(data?.error ?? "Lookup failed. Barcode set; fill remaining fields.");
      }
    } finally {
      setScanBusy(false);
      setScanOpen(false);
    }
  }

  async function onSubmit(values: ProductFormValues) {
    setSaveError(null);
    const payload = branchesEnabled ? values : { ...values, branch_id: null };
    try {
      if (product) {
        await update.mutateAsync({ id: product.id, values: payload });
      } else {
        await create.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch (e: unknown) {
      let msg = "Could not save product.";
      if (axios.isAxiosError(e)) {
        const data = e.response?.data as { error?: string } | undefined;
        if (data?.error) {
          msg = data.error;
        } else if (e.response?.status === 403) {
          msg = "Permission denied (need products.create / products.edit). Re-login after admin updates roles.";
        } else if (e.response?.status === 422) {
          msg = data?.error ?? "Validation failed.";
        }
      }
      setSaveError(msg);
    }
  }

  const pid = product?.id;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{product ? "Edit product" : "New product"}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid gap-3 sm:grid-cols-2"
        >
          <div className="sm:col-span-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" {...form.register("name")} />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="brand">Brand</Label>
            <Input id="brand" {...form.register("brand")} />
          </div>
          <div>
            <Label htmlFor="sku">SKU</Label>
            <Input id="sku" {...form.register("sku")} placeholder="Auto if empty" />
          </div>
          <div>
            <Label htmlFor="barcode">Barcode</Label>
            <div className="flex gap-2">
              <Input id="barcode" {...form.register("barcode")} className="flex-1" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openScan}
                title="Scan with USB barcode scanner or camera-equipped scanner"
              >
                <ScanLine className="mr-1 h-4 w-4" />
                Scan
              </Button>
            </div>
            {scanInfo ? (
              <p className="text-[11px] text-sky-700">{scanInfo}</p>
            ) : null}
          </div>
          <div>
            <Label>Category</Label>
            <select
              className={cn(
                "flex h-9 w-full rounded-lg border border-input bg-background px-3 text-sm",
              )}
              value={form.watch("category_id") ?? ""}
              onChange={(e) =>
                form.setValue(
                  "category_id",
                  e.target.value === "" ? null : Number(e.target.value),
                )
              }
            >
              <option value="">—</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Unit</Label>
            <select
              className="flex h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
              value={form.watch("unit_id") ?? ""}
              onChange={(e) =>
                form.setValue(
                  "unit_id",
                  e.target.value === "" ? null : Number(e.target.value),
                )
              }
            >
              <option value="">—</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.short_name})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="hsn">HSN</Label>
            <Input id="hsn" {...form.register("hsn_code")} />
          </div>
          <div>
            <Label htmlFor="gst">GST %</Label>
            <Input id="gst" type="number" step="0.01" {...form.register("gst_rate", { valueAsNumber: true })} />
          </div>
          <div>
            <Label htmlFor="cost">Cost</Label>
            <Input id="cost" type="number" step="0.01" {...form.register("cost_price", { valueAsNumber: true })} />
          </div>
          <div className="relative overflow-hidden rounded-xl border border-sky-200/55 bg-gradient-to-br from-sky-500/[0.09] via-card to-violet-500/[0.06] p-4 shadow-sm dark:border-sky-900/40 sm:col-span-2">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-12 -top-12 size-40 rounded-full bg-sky-400/10 blur-2xl"
            />
            <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1 pr-2">
                <Label className="text-sm font-semibold text-foreground">MRP includes GST</Label>
                <p className="max-w-xl text-xs text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Incl.</strong> — suggested selling = MRP ÷ (1 + GST%).{" "}
                  <strong className="text-foreground">Excl.</strong> — MRP is pre-tax; no auto fill from MRP. You can
                  always edit selling.
                </p>
              </div>
              <BinaryPillToggle
                value={mrpInclusive}
                disabled={saveBusy}
                onChange={(v) => setMrpInclusive(v)}
                offLabel="Excl."
                onLabel="Incl."
                offTitle="MRP excludes GST (manual selling)"
                onTitle="MRP includes GST (suggest selling from MRP)"
                ariaLabel="MRP includes GST"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="mrp">MRP {mrpInclusive ? "(incl. GST)" : ""}</Label>
            <Input id="mrp" type="number" step="0.01" {...form.register("mrp", { valueAsNumber: true })} />
            {mrpInclusive ? (
              <p className="text-[11px] text-muted-foreground">
                Suggested selling = MRP ÷ (1 + GST%). Edit selling below if you need a different taxable price.
              </p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="sell">Selling {mrpInclusive ? "(taxable; editable)" : "(taxable base)"}</Label>
            <Input
              id="sell"
              type="number"
              step="0.01"
              {...form.register("selling_price", { valueAsNumber: true })}
            />
            {mrpInclusive && (Number(gstVal) || 0) > 0 ? (
              <p className="text-[11px] text-muted-foreground">
                GST per unit ≈ ₹
                {(
                  (Number(mrpVal) || 0) -
                  (Math.round(((Number(mrpVal) || 0) / (1 + (Number(gstVal) || 0) / 100)) * 100) / 100)
                ).toFixed(2)}
              </p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="stock">Stock qty</Label>
            <Input id="stock" type="number" step="0.001" {...form.register("stock_qty", { valueAsNumber: true })} />
          </div>
          {branchesEnabled ? (
            <div>
              <Label>Branch</Label>
              <select
                className="flex h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
                value={form.watch("branch_id") ?? ""}
                onChange={(e) =>
                  form.setValue(
                    "branch_id",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
              >
                <option value="">—</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="relative overflow-hidden rounded-xl border border-border/80 bg-gradient-to-br from-muted/40 via-card to-violet-500/[0.05] p-4 shadow-sm sm:col-span-2">
            <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1 pr-2">
                <Label className="text-sm font-semibold text-foreground">Catalog & POS</Label>
                <p className="max-w-xl text-xs text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">On</strong> — visible in catalog and POS.{" "}
                  <strong className="text-foreground">Off</strong> — hidden from POS only; product stays in catalog.
                </p>
              </div>
              <Controller
                name="is_active"
                control={form.control}
                render={({ field }) => (
                  <BinaryPillToggle
                    value={field.value !== false}
                    disabled={saveBusy}
                    onChange={(v) => field.onChange(v)}
                    offLabel="Off"
                    onLabel="On"
                    ariaLabel="Product active"
                  />
                )}
              />
            </div>
          </div>
          {pid ? (
            <div className="sm:col-span-2">
              <Label htmlFor="img">Image (max 2MB)</Label>
              <Input
                id="img"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) {
                    return;
                  }
                  try {
                    await uploadImg.mutateAsync({ id: pid, file: f });
                  } catch {
                    /* ignore */
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">Upload replaces image_url on server.</p>
            </div>
          ) : null}
          {saveError ? (
            <p className="text-sm text-destructive sm:col-span-2" role="alert">
              {saveError}
            </p>
          ) : null}
          <div className="mt-2 flex justify-end gap-2 border-t pt-4 sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || update.isPending}>
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>

      <Dialog open={scanOpen} onOpenChange={setScanOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Scan barcode</DialogTitle>
            <DialogDescription>
              Aim a USB scanner at the product barcode. The reader types the code and presses Enter automatically. Or paste/type the code and press Enter.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border-2 border-dashed border-sky-400 bg-sky-50/40 p-4 text-center">
              <ScanLine className="mx-auto mb-2 h-8 w-8 text-sky-600" />
              <p className="text-xs font-medium text-sky-900">Ready to scan…</p>
            </div>
            <Label htmlFor="scan-input">Code</Label>
            <Input
              id="scan-input"
              ref={scanInputRef}
              value={scanBuf}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              disabled={scanBusy}
              placeholder="Scan or type barcode"
              onChange={(e) => setScanBuf(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void applyScannedBarcode(scanBuf);
                }
              }}
            />
            <p className="text-[11px] text-muted-foreground">
              Tip: most USB barcode readers act as keyboards. Click in the box above first if your reader does not auto-trigger.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => setScanOpen(false)} disabled={scanBusy}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void applyScannedBarcode(scanBuf)}
              disabled={scanBusy || scanBuf.trim() === ""}
            >
              {scanBusy ? "Looking up…" : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
